import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

/* ---------- Wind Turbine ---------- */
function Turbine({ position = [0, 0, 0], scale = 1, speed = 1.2 }) {
  const rotorRef = useRef();
  useFrame((_, dt) => {
    if (rotorRef.current) rotorRef.current.rotation.z += dt * speed;
  });
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.55, 0.75, 0.3, 24]} />
        <meshStandardMaterial color="#e7e5e4" roughness={0.7} />
      </mesh>
      <mesh position={[0, 3.2, 0]}>
        <cylinderGeometry args={[0.18, 0.36, 6.2, 24]} />
        <meshStandardMaterial color="#f5f5f4" roughness={0.4} metalness={0.15} />
      </mesh>
      <mesh position={[0, 6.5, 0.25]}>
        <boxGeometry args={[0.6, 0.55, 1.2]} />
        <meshStandardMaterial color="#f5f5f4" roughness={0.4} metalness={0.2} />
      </mesh>
      <group ref={rotorRef} position={[0, 6.5, 0.9]}>
        <mesh>
          <sphereGeometry args={[0.22, 16, 16]} />
          <meshStandardMaterial color="#e7e5e4" />
        </mesh>
        {[0, 1, 2].map((i) => {
          const angle = (i / 3) * Math.PI * 2;
          return (
            <mesh
              key={i}
              position={[Math.cos(angle) * 1.6, Math.sin(angle) * 1.6, 0]}
              rotation={[0, 0, angle + Math.PI / 2]}
            >
              <boxGeometry args={[0.14, 3.2, 0.08]} />
              <meshStandardMaterial color="#fafaf9" roughness={0.5} />
            </mesh>
          );
        })}
      </group>
    </group>
  );
}

/* ---------- Solar Panel ---------- */
function SolarPanel({ position = [0, 0, 0] }) {
  return (
    <group position={position}>
      <mesh position={[-0.9, 0.3, 0]}>
        <boxGeometry args={[0.06, 0.6, 0.06]} />
        <meshStandardMaterial color="#57534e" />
      </mesh>
      <mesh position={[0.9, 0.3, 0]}>
        <boxGeometry args={[0.06, 0.6, 0.06]} />
        <meshStandardMaterial color="#57534e" />
      </mesh>
      <mesh position={[0, 0.7, 0]} rotation={[-Math.PI / 6, 0, 0]}>
        <boxGeometry args={[2.2, 0.05, 1.3]} />
        <meshStandardMaterial
          color="#1e3a5f"
          metalness={0.5}
          roughness={0.35}
          emissive="#0a1a30"
          emissiveIntensity={0.2}
        />
      </mesh>
    </group>
  );
}

function SolarArray({ position = [0, 0, 0] }) {
  const panels = [];
  for (let i = -1; i <= 1; i++) {
    for (let j = -1; j <= 1; j++) {
      panels.push([i * 2.6, 0, j * 1.9]);
    }
  }
  return (
    <group position={position}>
      {panels.map((p, idx) => (
        <SolarPanel key={idx} position={p} />
      ))}
    </group>
  );
}

/* ---------- Data centre pavilion (Aethron HPC hall) ---------- */
function DataCenter({ position = [0, 0, 0] }) {
  const stripRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  useFrame((state) => {
    // Pulse the light strips gently to signal live compute
    const t = state.clock.getElapsedTime();
    stripRefs.forEach((r, i) => {
      if (r.current) {
        const v = 0.5 + 0.5 * Math.sin(t * 2 + i * 0.7);
        r.current.material.emissiveIntensity = 0.6 + v * 0.8;
      }
    });
  });
  return (
    <group position={position}>
      {/* Roof/main volume */}
      <mesh position={[0, 1.4, 0]}>
        <boxGeometry args={[8.5, 2.4, 3.6]} />
        <meshStandardMaterial color="#2b3a2f" roughness={0.7} metalness={0.15} />
      </mesh>
      {/* Green roof strip */}
      <mesh position={[0, 2.62, 0]}>
        <boxGeometry args={[8.5, 0.08, 3.6]} />
        <meshStandardMaterial color="#3f6b3d" roughness={1} />
      </mesh>
      {/* Front facade */}
      <mesh position={[0, 1.4, 1.81]}>
        <boxGeometry args={[8.4, 2.3, 0.05]} />
        <meshStandardMaterial color="#0f1a12" roughness={0.6} metalness={0.3} />
      </mesh>
      {/* Emissive light strips */}
      {[-3.2, -1.9, -0.6, 0.6, 1.9, 3.2].map((x, i) => (
        <mesh key={i} ref={stripRefs[i]} position={[x, 1.4, 1.84]}>
          <boxGeometry args={[0.5, 1.6, 0.02]} />
          <meshStandardMaterial
            color="#95D5B2"
            emissive="#2D6A4F"
            emissiveIntensity={1}
            roughness={0.4}
          />
        </mesh>
      ))}
      {/* Concrete plinth */}
      <mesh position={[0, 0.1, 0]}>
        <boxGeometry args={[9, 0.2, 4]} />
        <meshStandardMaterial color="#6b6b6b" roughness={0.9} />
      </mesh>
      {/* Heat-export pipe */}
      <mesh position={[3.2, 0.35, 2.2]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.15, 0.15, 3.5, 12]} />
        <meshStandardMaterial color="#B45309" roughness={0.6} />
      </mesh>
    </group>
  );
}

/* ---------- Terrain ---------- */
function Ground() {
  const geom = useMemo(() => {
    const g = new THREE.PlaneGeometry(120, 120, 60, 60);
    const pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z =
        Math.sin(x * 0.15) * 0.4 +
        Math.cos(y * 0.2) * 0.35 +
        Math.sin((x + y) * 0.08) * 0.6;
      pos.setZ(i, z);
    }
    g.computeVertexNormals();
    return g;
  }, []);
  return (
    <mesh geometry={geom} rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.2, 0]}>
      <meshStandardMaterial color="#3f6b3d" roughness={1} />
    </mesh>
  );
}

/* ---------- Distant hills ---------- */
function Hill({ position, scale, color }) {
  return (
    <mesh position={position} scale={scale}>
      <sphereGeometry args={[1, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
      <meshStandardMaterial color={color} roughness={1} />
    </mesh>
  );
}

/* ---------- Cloud puffs (simple, no drei Clouds) ---------- */
function CloudPuff({ position, scale = 1 }) {
  const ref = useRef();
  useFrame((_, dt) => {
    if (ref.current) ref.current.position.x += dt * 0.15;
    if (ref.current && ref.current.position.x > 40) ref.current.position.x = -40;
  });
  return (
    <group ref={ref} position={position} scale={scale}>
      {[
        [0, 0, 0, 1.5],
        [1.4, 0.2, 0, 1.2],
        [-1.3, 0.1, 0, 1.1],
        [0.5, -0.3, 0.4, 1.0],
      ].map(([x, y, z, r], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[r, 12, 10]} />
          <meshStandardMaterial color="#ffffff" roughness={1} transparent opacity={0.9} />
        </mesh>
      ))}
    </group>
  );
}

/* ---------- Main Scene ---------- */
export default function EnergyScene() {
  return (
    <Canvas
      dpr={[1, 1.5]}
      camera={{ position: [14, 8, 18], fov: 45 }}
      gl={{ antialias: true, powerPreference: "high-performance" }}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <color attach="background" args={["#DDECD6"]} />
      <fog attach="fog" args={["#DDECD6", 25, 75]} />

      <ambientLight intensity={0.7} color="#fff1d6" />
      <directionalLight position={[15, 20, 8]} intensity={1.4} color="#ffe4b0" />
      <directionalLight position={[-8, 6, -10]} intensity={0.4} color="#b7d7c8" />
      <hemisphereLight args={["#ffe8c8", "#3f6b3d", 0.5]} />

      <Ground />

      {/* Distant hills */}
      <Hill position={[-25, -0.5, -30]} scale={[12, 5, 12]} color="#1f3d24" />
      <Hill position={[20, -0.5, -35]} scale={[15, 6, 15]} color="#17301c" />
      <Hill position={[0, -0.5, -45]} scale={[20, 8, 20]} color="#0f2416" />

      <Turbine position={[-9, 0, -4]} scale={1} speed={1.4} />
      <Turbine position={[-3, 0, -8]} scale={0.9} speed={1.1} />
      <Turbine position={[7, 0, -6]} scale={1.15} speed={1.6} />
      <Turbine position={[12, 0, 2]} scale={0.85} speed={1.2} />

      <SolarArray position={[-4, 0, 6]} />
      <SolarArray position={[6, 0, 8]} />

      {/* Aethron HPC hall — the heart of the scene */}
      <DataCenter position={[0, 0, -2]} />

      <CloudPuff position={[-10, 12, -15]} scale={1.3} />
      <CloudPuff position={[15, 14, -20]} scale={1.6} />
      <CloudPuff position={[3, 15, -25]} scale={1.1} />

      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={12}
        maxDistance={35}
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI / 2.15}
        autoRotate
        autoRotateSpeed={0.4}
      />
    </Canvas>
  );
}
